import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateField, FileActions, FileField, FormErrorSummary, SelectField, StatusBadge, VersionHistory } from '@/business';
import { Alert, Button, Dialog, DialogContent, HistoryIcon, Table, TBody, TD, TH, THead, TR, cn, scrollRegion, toast } from '@/design-system';
import { apiErrorMessage } from '@/shared/api';
import { Can } from '@/shared/auth';
import { addDaysToDateOnly, todayInDubai } from '@/shared/lib/dates';
import { formatDate } from '@/shared/lib/format';
import { lookupsQuery } from '@/shared/lookups';

import { useUploadCustomerDocument } from '../api/mutations';
import { customerDocumentVersionsQuery, fetchCustomerDocumentLink, fetchCustomerDocumentVersionLink } from '../api/queries';
import { CUSTOMER_DOCUMENT_TYPES, documentUploadSchema, toCustomerDocumentInput, toDocumentUploadValues, type DocumentUploadValues } from '../forms/maintenance';
import { formLevelError } from './serverErrors';

type Doc = Schema<'CustomerDocument'>;

/**
 * Customer-level documents, reused by every deal. Status is computed by the server: a document past its expiry date
 * is Expired (never shown as valid) and needs new evidence.
 */
export function CustomerDocumentsTable({ customerId, documents, readOnly, onUpload, className }: { customerId: string; documents: Doc[]; readOnly?: boolean; onUpload: (doc: Doc | null) => void; className?: string }) {
  const { t } = useTranslation('customers');
  const [history, setHistory] = useState<string | null>(null);
  if (!documents.length) return <p className={cn('py-2 text-body text-ink-secondary', className)}>{t('documents.empty')}</p>;
  return (
    <div {...scrollRegion(t('record.documents'))} className={cn('overflow-x-auto border-x border-b border-border', className)}>
      <Table>
        <THead>
          <tr>
            <TH>{t('documents.columns.document')}</TH>
            <TH>{t('documents.columns.expiry')}</TH>
            <TH>{t('documents.columns.status')}</TH>
            <TH>{t('documents.columns.evidence')}</TH>
            <TH align="end">{t('documents.columns.actions')}</TH>
          </tr>
        </THead>
        <TBody>
          {documents.map((d) => (
            <Fragment key={d.id}>
              <TR>
                <TD className="font-medium">{d.name}</TD>
                <TD className={d.status === 'expired' ? 'text-negative' : 'text-ink-secondary'}>{d.expiresOn ? formatDate(d.expiresOn) : '—'}</TD>
                <TD>
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    {d.scanStatus === 'infected' || d.scanStatus === 'failed' ? <StatusBadge registry="fileScanStatus" value={d.scanStatus} /> : <StatusBadge registry="documentStatus" value={d.status} />}
                    {d.requiresNewEvidence ? <span className="text-meta text-ink-muted">{t('documents.needsEvidence')}</span> : null}
                  </span>
                </TD>
                <TD className="text-ink-secondary">
                  {d.fileName ? (
                    <span className="flex flex-col">
                      <bdi dir="ltr" className="truncate text-meta">{d.fileName}</bdi>
                      <span className="text-meta text-ink-muted">
                        {t('documents.version', { version: d.versionNo ?? 1, date: d.uploadedAt ? formatDate(d.uploadedAt) : '—' })}
                        {d.issuedOn ? ` · ${t('documents.issued', { date: formatDate(d.issuedOn) })}` : ''}
                      </span>
                    </span>
                  ) : (
                    '—'
                  )}
                </TD>
                <TD align="end">
                  <span className="inline-flex items-center justify-end gap-1">
                    {d.fileName ? <FileActions fileName={d.fileName} scanStatus={d.scanStatus ?? null} fetchLink={(disposition) => fetchCustomerDocumentLink(customerId, d.id, disposition)} /> : null}
                    {d.fileName ? (
                      <Button size="xs" variant="ghost" icon={<HistoryIcon size={13} />} aria-expanded={history === d.id} aria-label={t('documents.historyLabel', { name: d.name })} onClick={() => setHistory(history === d.id ? null : d.id)}>
                        {history === d.id ? t('files.versions.hide', { ns: 'common' }) : t('files.versions.show', { ns: 'common' })}
                      </Button>
                    ) : null}
                    {readOnly ? null : (
                      <Can permission="document.upload">
                        <Button size="xs" variant={d.requiresNewEvidence ? 'secondary' : 'ghost'} onClick={() => onUpload(d)} aria-label={t('documents.replaceLabel', { name: d.name })}>
                          {d.requiresNewEvidence ? t('documents.uploadNew') : t('documents.replace')}
                        </Button>
                      </Can>
                    )}
                  </span>
                </TD>
              </TR>
              {history === d.id ? (
                <tr>
                  <td colSpan={5} className="border-b border-border bg-surface px-3 py-2">
                    <CustomerDocumentVersions customerId={customerId} documentId={d.id} />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

/** Version history of one customer document (loaded when opened). */
function CustomerDocumentVersions({ customerId, documentId }: { customerId: string; documentId: string }) {
  const versions = useQuery(customerDocumentVersionsQuery(customerId, documentId));
  return (
    <VersionHistory
      versions={versions.data}
      loading={versions.isPending}
      error={versions.isError ? apiErrorMessage(versions.error) : null}
      fetchVersionLink={(versionId, disposition) => fetchCustomerDocumentVersionLink(customerId, documentId, versionId, disposition)}
    />
  );
}

/** Upload or replace one customer document (type, file, issue and expiry dates → dialog). */
export function CustomerDocumentDialog({ customerId, document, onClose }: { customerId: string; document: Doc | null; onClose: () => void }) {
  const { t } = useTranslation('customers');
  const today = todayInDubai();
  const lookups = useQuery(lookupsQuery);
  const upload = useUploadCustomerDocument(customerId);
  const [progress, setProgress] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const types = (lookups.data?.documentTypes ?? []).filter((d) => (CUSTOMER_DOCUMENT_TYPES as readonly string[]).includes(d.code));
  const expiring = useMemo(() => types.filter((d) => d.hasExpiry).map((d) => d.code), [types]);
  const schema = useMemo(() => documentUploadSchema(today, expiring), [today, expiring]);
  const form = useForm<DocumentUploadValues>({ resolver: zodResolver(schema), defaultValues: toDocumentUploadValues(document) });
  const typeCode = form.watch('typeCode');
  const fileError = form.formState.errors.file?.message;
  const uploading = progress !== null && upload.isPending;

  const submit = form.handleSubmit(async (v) => {
    setFormError(null);
    if (!v.file) return;
    try {
      await upload.mutateAsync({ file: v.file, body: toCustomerDocumentInput(v, expiring), onProgress: setProgress });
      toast.done(document ? t('documents.replaced', { name: document.name }) : t('documents.added'));
      onClose();
    } catch (e) {
      setProgress(null);
      setFormError(formLevelError(form, e, ['typeCode', 'expiresOn', 'issuedOn', 'file']));
    }
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        size="form"
        title={document ? t('documents.replaceTitle', { name: document.name }) : t('documents.addTitle')}
        description={t('documents.dialogBody')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={upload.isPending} onClick={() => void submit()}>
              {uploading ? t('documents.uploading', { percent: progress }) : t('documents.save')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {formError ? <FormErrorSummary title={formError} /> : null}
          {document?.status === 'expired' ? <Alert tone="warning" title={t('documents.expiredTitle', { date: document.expiresOn ? formatDate(document.expiresOn) : '—' })}>{t('documents.expiredBody')}</Alert> : null}
          <SelectField control={form.control} name="typeCode" label={t('documents.type')} disabled={Boolean(document)} options={types.map((d) => ({ value: d.code, label: d.name }))} placeholder={t('form.selectPlaceholder', { ns: 'common' })} />
          <FileField
            label={t('documents.file')}
            purpose="document"
            error={fileError ? t(fileError as 'form.required', { ns: 'common' }) : undefined}
            onChange={(file) => form.setValue('file', file, { shouldValidate: form.formState.isSubmitted })}
          />
          <DateField control={form.control} name="issuedOn" label={t('documents.issuedOn')} max={today} optional help={t('documents.issuedHelp')} />
          {expiring.includes(typeCode) ? <DateField control={form.control} name="expiresOn" label={t('documents.expiresOn')} min={addDaysToDateOnly(today, 1)} help={t('documents.expiresHelp')} /> : null}
          <button type="submit" hidden />
        </form>
      </DialogContent>
    </Dialog>
  );
}
